// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import Button from './Button';
import type { LDFlagName } from '../../config/launchDarklyFlags';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useFlagsReady } from '../../hooks/useFlagsReady';
import { useFlagsUnreachable } from '../../hooks/useFlagsUnreachable';
import { useKillSwitch } from '../../hooks/useKillSwitch';

interface FlagRouteProps {
  flag: LDFlagName;
  /** Rendered when the flag is off, usually a `<Navigate replace />`. */
  fallback: React.ReactNode;
  children: React.ReactNode;
  /**
   * Hold the route until LaunchDarkly has answered (default). A kill switch that
   * defaults on passes `false`: until ready it decides from the environment value or
   * the catalog default (see useKillSwitch), not from a possibly stale read, so a
   * LaunchDarkly that never answers (readiness never settles once its retries are
   * spent) cannot leave the page blank, and a deep link isn't lost to a stale `false`.
   */
  waitForFlags?: boolean;
}

/**
 * What a gated route shows when LaunchDarkly was given up on: a notice with a reload, not a
 * blank page and not a redirect decided on a default nobody confirmed (AB#35012).
 */
function FlagsUnavailable() {
  return (
    <div role="alert" style={{ padding: '2rem' }}>
      <p>Helios couldn&apos;t load its feature settings, so this page can&apos;t be shown yet.</p>
      <Button variant="ghost" size="small" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  );
}

/**
 * The route half of a flagged surface; `useFlagOn` hides its nav entry.
 * Gating only the nav would leave the page one URL away.
 *
 * Renders nothing until LaunchDarkly has answered for this identity, so a
 * deep link isn't sent away on the catalog default before the real value
 * arrives (AB#29037), then the page if the flag is on, `fallback` if not.
 * If LaunchDarkly is given up on (retries spent) it shows a notice with a reload instead of
 * staying blank (AB#35012); it still doesn't guess from a default.
 */
export default function FlagRoute({
  flag,
  fallback,
  children,
  waitForFlags = true,
}: FlagRouteProps) {
  const ready = useFlagsReady();
  const unreachable = useFlagsUnreachable();
  const flagOn = useFeatureFlag(flag);
  const killSwitchOn = useKillSwitch(flag);
  if (waitForFlags && !ready) return unreachable ? <FlagsUnavailable /> : null;
  return <>{(waitForFlags ? flagOn : killSwitchOn) ? children : fallback}</>;
}
